import React, { useState, useEffect, useRef } from "react";

import { Routes, Route, useSearchParams, Navigate, useLocation } from "react-router-dom";

// Layout/UI
import Header from "./Header";
import { Box, Container, Icon } from "@metrea-ui/v2";
import { ChevronUpFilled } from "@fluentui/react-icons";

import { Landing } from "src/pages/Landing";
import { Profile } from "src/pages/Profile";
import { Blank } from "src/pages/Blank";
import { Home } from "src/pages/Home";
import { Designer } from "src/pages/Designer";
import { About } from "src/pages/About";
import { Test } from "src/pages/Test";
import { NoAccess } from "src/pages/NoAccess";
import { Baseline } from "src/pages/Baseline";
import { Scenario } from "src/pages/Scenario";
import { Auction } from "src/pages/Auction";
import { Admin, User, MyProfile, UserActivity, UserGroup } from "src/pages/Admin";

import { LoadingIndicator } from "src/components/UI/common/LoadIndicator";
import { TimeoutLogic } from "../TimeoutLogic/TimeoutLogic";
import HideOnScroll from "src/components/Layout/HideOnScroll";

import { mColor } from "src/utils/themeUtils";
import { isUserLogin } from "src/utils/sessionUtils";
import { isDevSiteNow } from "src/utils/debugUtils";
import { isAdmin, isDeveloper, isDesigner, isStrat } from "src/utils/sessionUtils";

function ScrollTop({ scrollContainerRef }) {
	const [show, setShow] = useState(false);

	useEffect(() => {
		const container = scrollContainerRef?.current;
		if (!container) return;

		const handleScroll = () => {
			setShow(container.scrollTop > container.clientHeight * 0.5);
		};

		handleScroll();
		container.addEventListener("scroll", handleScroll, { passive: true });

		return () => container.removeEventListener("scroll", handleScroll);
	}, [scrollContainerRef]);

	const handleClick = () => {
		const container = scrollContainerRef?.current;
		if (!container) return;
		container.scrollTo({
			top: 0,
			behavior: "smooth",
		});
	};

	return (
		<Box
			position="fixed"
			bottom="35px"
			right="40px"
			zIndex="tooltip"
			onClick={handleClick}
			role="presentation"
			cursor="pointer"
			borderRadius="full"
			bg={mColor.MidnightBlue}
			color={mColor.White}
			w="40px"
			h="40px"
			display="flex"
			alignItems="center"
			justifyContent="center"
			boxShadow="lg"
			transition="all 0.2s ease-out"
			opacity={show ? 1 : 0}
			transform={show ? "translateY(0)" : "translateY(8px)"}
			pointerEvents={show ? "auto" : "none"}
			_hover={{
				bg: mColor.StonyBlue,
				transform: "translateY(0) scale(1.05)",
			}}
		>
			<Icon as={ChevronUpFilled} boxSize="28px" />
		</Box>
	);
}

const Pages = (props) => {
	const [searchParams, setSearchParams] = useSearchParams();
	const getCode = searchParams.get("getCode");

	/* remove the search parameter (uuid) from the URL */
	useEffect(() => {
		const uuid = searchParams.get("uuid");
		if (uuid) {
			// Delete each query param
			searchParams.delete("uuid");

			// Update state after
			setSearchParams(searchParams);
		}
	}, [searchParams, setSearchParams]);

	return (
		<>
			<Routes>
				{!getCode && <Route path="/" element={<Landing />} />}

				<Route path="/home" element={<Navigate to="/scenario" />} />
				<Route path="/app" element={<Home />} />
				<Route path="/profile" element={<Profile />} />
				<Route path="/blank" element={<Blank />} />
				<Route path="/about" element={<About />} />
				<Route path="/auction" element={isDeveloper() || isStrat() ? <Auction /> : <NoAccess />} />

				{
					// Test page is only available to the devTeam
					(isDeveloper() || isDevSiteNow()) && <Route path="/test" element={<Test />} />
				}

				<Route path="/baseline" element={<Baseline />} />
				<Route path="/baseline/:id" element={<Baseline />} />
				<Route path="/scenario" element={<Scenario />} />
				<Route path="/scenario/:id" element={<Scenario />} />
				<Route
					path="/baseline/edit"
					element={isDeveloper() || isAdmin() || isDesigner() ? <Baseline /> : <NoAccess />}
				/>
				<Route
					path="/baseline/edit/:id"
					element={isDeveloper() || isAdmin() || isDesigner() ? <Baseline /> : <NoAccess />}
				/>
				<Route
					path="/designer"
					element={isDeveloper() || isAdmin() || isDesigner() ? <Designer /> : <NoAccess />}
				/>

				<Route path="/admin" element={isDeveloper() || isAdmin() ? <Admin /> : <NoAccess />} />
				<Route path="/admin/user" element={isDeveloper() || isAdmin() ? <User /> : <NoAccess />} />
				<Route
					path="/admin/user/:id"
					element={isDeveloper() || isAdmin() ? <User /> : <NoAccess />}
				/>
				<Route
					path="/admin/usergroup"
					element={isDeveloper() || isAdmin() ? <UserGroup /> : <NoAccess />}
				/>
				<Route
					path="/admin/activity"
					element={isDeveloper() || isAdmin() ? <UserActivity /> : <NoAccess />}
				/>

				{(isDeveloper() || isAdmin()) && (
					<>
						<Route path="/admin" element={<Admin />} />
						<Route path="/admin/user" element={<User />} />
						<Route path="/admin/activity" element={<UserActivity />} />
					</>
				)}
				{isUserLogin() && (
					<>
						<Route path="/myprofile" element={<MyProfile />} />
					</>
				)}
			</Routes>
		</>
	);
};

export const PageLayout = ({ isOnTop, isDisplay, session }) => {
	const contentScrollRef = useRef(null);
	const location = useLocation();
	const landingPage = location.pathname === "/" && !location.search.includes("getCode=true");

	const pageContentClassName = ["page-content", landingPage ? "landing-page" : ""].join(" ");
	const contentHeight = "calc(100vh - 152px) !important";

	/* MetreaUI Integration */
	const MetreaUI_background = {
		backgroundImage: "linear-gradient(rgba(255,255,255,1), rgba(255,255,255,0)), url(/Desktop_6.jpg)",
		backgroundPosition: "bottom center",
		backgroundRepeat: "no-repeat",
		backgroundSize: "cover",
		backgroundAttachment: "fixed",
		padding: "0",
		height: "100vh",
		minHeight: "100vh",
		overflow: "hidden",
		display: "flex",
		flexDirection: "column",
	};

	return (
		<>
			<div className={pageContentClassName} style={{ ...MetreaUI_background }}>
				{isUserLogin() && <TimeoutLogic />}

				{/* Show a full-page spinner whenever trackPromise() is running (ex. during update/create dispatches) */}
				<LoadingIndicator />

				{/* <CssBaseline /> */}

				<span id="back-to-top-anchor" />

				<HideOnScroll isOnTop={isOnTop} scrollContainerRef={contentScrollRef}>
					<Box boxShadow={isOnTop ? "none" : "lg"} display={isDisplay} bg="white">
						<Header session={session} isOnTop={isOnTop} />
					</Box>
				</HideOnScroll>

				<Box ref={contentScrollRef} flex="1" minH={0} overflowY="auto" overflowX="hidden">
					<Container
						maxW="100%" // or maxW="none"
						w="100%" // ensure full width
						data-name="contentContainer"
						m={0}
						p={0}
						px={0} // override Container's default horizontal padding
						minH={contentHeight}
						pt={0}
					>
						<Box className="pt-[50px] pb-[28px]" px="var(--rail-x)">
							<Pages session={session} />
							<br />
						</Box>
					</Container>
				</Box>

				<ScrollTop scrollContainerRef={contentScrollRef} />
			</div>
		</>
	);
};
